// Web Push Notification Manager for ActivityLink
// Handles push notifications on web browsers using Firebase Cloud Messaging

import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
} from "firebase/messaging";
import { analytics, app, crashlytics } from "../services/firebase";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../services/firebase";
import { platform } from "os";

// VAPID key for web push notifications
// Get this key to Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
const VAPID_KEY =
  "BE-W3Qt4c1JE9f2OnxCA-mY0RSRUhItqMkgvyDMtwxTqoUPZRhrTDueOquGMzhPyp-0qy6ewpEiQWVNNRBxuQAg";

// Note: Replace the above VAPID_KEY with your actual key from Firebase Console

export class WebPushNotificationManager {
  private static messaging: any = null;
  private static currentToken: string | null = null;
  private static isInitialized = false;

  /**
   * Initialize web push notifications
   * Should be called once when user logs in
   */
  static async initialize(userId?: string): Promise<boolean> {
    try {
      // Check if browser supports notifications
      if (!("Notification" in window)) {
        return false;
      }

      // Check if browser supports service workers
      if (!("serviceWorker" in navigator)) {
        return false;
      }

      // Check if Firebase Messaging is supported
      const messagingSupported = await isSupported();
      if (!messagingSupported) {
        return false;
      }

      if (this.isInitialized) {
        return true;
      }

      // Initialize Firebase Messaging
      this.messaging = getMessaging(app);

      // Register service worker
      const registration = await this.registerServiceWorker();
      if (!registration) {
        crashlytics.logError(
          "Failed to register service worker",
          "webPushNotificationManager.ts",
          new Error("Failed to register service worker"),
        );
        return false;
      }

      // Check if permission was already granted (don't request automatically)
      const permission = Notification.permission;
      let token: string | null = null;

      if (permission === "granted") {

        // Get FCM token since permission is already granted
        token = await this.getMessagingToken(registration);

        if (!token) {
          crashlytics.logError(
            "Failed to get FCM token",
            "webPushNotificationManager.ts",
            new Error("Failed to get FCM token"),
          );
          return false;
        }
      } else {
        // Don't request permission automatically - must be user-initiated
      }

      if (token) {
        this.currentToken = token;
        // Save token to user profile in Firestore
        if (userId) {
          await this.saveTokenToUser(userId, token);
        }

        // Set up foreground message handler
        this.setupForegroundHandler();

        // Listen for messages from service worker
        this.setupServiceWorkerMessageListener();
      }

      this.isInitialized = true;
      return true;
    } catch (error) {
      crashlytics.logError(
        `Error in initialize: ${JSON.stringify(error?.message)}`,
        "webPushNotificationManager.ts",
        error,
      );
      return false;
    }
  }

  /**
   * Register the Firebase Messaging service worker
   */
  private static async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    try {
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
        { scope: "/" },
      );


      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      return registration;
    } catch (error) {
      crashlytics.logError(
        `Error in registerServiceWorker: ${JSON.stringify(error?.message)}`,
        "webPushNotificationManager.ts",
        error,
      );
      return null;
    }
  }

  /**
   * Request notification permission
   * IMPORTANT: Must be called in response to a user gesture (button click, etc.)
   */
  private static async requestPermission(): Promise<boolean> {
    try {
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        return true;
      } else if (permission === "denied") {
        return false;
      } else {
        return false;
      }
    } catch (error) {
      crashlytics.logError(
        `Error in requestPermission: ${JSON.stringify(error?.message)}`,
        "webPushNotificationManager.ts",
        error,
      );
      return false;
    }
  }

  /**
   * Get FCM registration token
   */
  private static async getMessagingToken(
    registration: ServiceWorkerRegistration,
  ): Promise<string | null> {
    try {
      const token = await getToken(this.messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        return token;
      } else {
        crashlytics.logError(
          "No registration token available",
          "webPushNotificationManager.ts",
          new Error("No registration token available"),
        );
        return null;
      }
    } catch (error) {
      crashlytics.logError(
        `Error in getMessagingToken: ${JSON.stringify(error?.message)}`,
        "webPushNotificationManager.ts",
        error,
      );
      return null;
    }
  }

  /**
   * Save FCM token to user's profile in Firestore
   */
  private static async saveTokenToUser(userId: string, token: string) {
    try {
      const userRef = doc(db, "users", userId);
      // Use arrayUnion to add the new token to the fcmTokens array without duplicates
      await updateDoc(userRef, {
        fcmTokens: arrayUnion(token),
        fcmTokenUpdatedAt: new Date().toISOString(),
        platform: "web",
      });
      analytics.setUserProperty(
        "fcmTokenStartWith",
        token ? token.substring(0, 10) : "none",
      );
      analytics.logEvent("tokenRegistered", {
        timestamp: new Date().toISOString(),
        fcmTokenStartWith: token ? token.substring(0, 10) : "none",
        platform: "web",
      });
      crashlytics.log(
        `FCM Token registered for user: ${userId}, token starts with: ${token ? token.substring(0, 10) : "none"}`,
      );
    } catch (error) {
      crashlytics.logError(
        `Error in saveTokenToUser: ${JSON.stringify(error?.message)}`,
        "webPushNotificationManager.ts",
        error,
      );
    }
  }

  /**
   * Set up handler for foreground messages (when app is in focus)
   */
  private static setupForegroundHandler() {
    if (!this.messaging) return;

    onMessage(this.messaging, (payload) => {

      // Show notification using Web Notification API
      if (Notification.permission === "granted") {
        const notificationTitle = payload.notification?.title || "Who's in?";
        const notificationOptions: NotificationOptions = {
          body: payload.notification?.body || "You have a new notification",
          icon: "/logo.png",
          badge: "/logo.png",
          data: payload.data || {},
          tag: payload.data?.chatId || payload.data?.activityId || "general",
          requireInteraction: false,
        };

        // Create notification
        const notification = new Notification(
          notificationTitle,
          notificationOptions,
        );

        // Handle notification click
        notification.onclick = (event) => {
          event.preventDefault();
          notification.close();

          const link = payload.data?.link || "/";
          window.focus();
          window.location.href = link;
        };
      }
    });

  }

  /**
   * Listen for messages from service worker (notification clicks)
   */
  private static setupServiceWorkerMessageListener() {
    if (!navigator.serviceWorker) return;

    navigator.serviceWorker.addEventListener("message", (event) => {

      if (event.data?.type === "notification-click") {
        const link = event.data.link || "/";

        // Navigate to the link
        if (window.location.pathname !== link) {
          window.location.href = link;
        }
      }
    });

  }

  /**
   * Get current FCM token
   */
  static getToken(): string | null {
    return this.currentToken;
  }

  /**
   * Check notification permission status
   */
  static getPermissionStatus(): NotificationPermission {
    if ("Notification" in window) {
      return Notification.permission;
    }
    return "default";
  }

  /**
   * Get status of web push notifications
   */
  static async getStatus() {
    return {
      platform: "web",
      supported: "Notification" in window && "serviceWorker" in navigator,
      permission: this.getPermissionStatus(),
      token: this.currentToken,
      initialized: this.isInitialized,
    };
  }

  /**
   * Remove FCM token (for logout)
   */
  static async removeToken(userId?: string) {
    // 1. Check if we have a user ID and the current device's token
    if (userId && this.currentToken) {
      try {
        const userRef = doc(db, "users", userId);

        // CRITICAL FIX: Use arrayRemove to remove the *specific* token
        // from the fcmTokens array, leaving other devices intact.
        await updateDoc(userRef, {
          fcmTokens: arrayRemove(this.currentToken),
          fcmTokenUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error in removeToken: ${JSON.stringify(error?.message)}`,
          "webPushNotificationManager.ts",
          error,
        );
      }
    }

    // 2. Clean up local state
    this.currentToken = null; // Standardizing to 'this.token'
    this.isInitialized = false;

    // 3. Unregister service worker (Only relevant for web/PWA)
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (
          registration.active?.scriptURL.includes("firebase-messaging-sw.js")
        ) {
          await registration.unregister();
        }
      }
    }
  }

  /**
   * Request permission again if previously denied/dismissed
   */
  static async requestPermissionAgain(): Promise<boolean> {
    return await this.requestPermission();
  }
}
